import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient, { setUnauthorizedHandler } from '../api/config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// "Remember me" controls whether the cached user is kept in localStorage
// (survives browser restarts) or sessionStorage (cleared when the browser
// closes, matching the shorter-lived session cookie set for that login).
// Absent/legacy 'remember_me' preference defaults to localStorage so
// existing sessions and Azure SSO (which never opts out) keep working.
const getStorage = () => (localStorage.getItem('remember_me') === 'false' ? sessionStorage : localStorage);

const readStoredUser = () => {
  try {
    const stored = getStorage().getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser);
  const [loading, setLoading] = useState(() => !getStorage().getItem('user'));

  const handleUnauthorized = () => {
    setUser(null);
    getStorage().removeItem('user');
  };

  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);


  // has expired, this gets 401 and the interceptor redirects to login.
  const SESSION_CHECK_INTERVAL_MS = 45 * 1000;
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      apiClient.get('/api/auth/user/')
        .then(res => {
          if (res.data.success) {
            setUser(res.data.user);
            getStorage().setItem('user', JSON.stringify(res.data.user));
          }
        })
        .catch(() => {});
    }, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(t);
  }, [user]);

  const checkAuth = async () => {
    try {
      // Show cached data immediately so the UI doesn't block, but always
      // verify with the server — stale localStorage (wrong role, missing
      // client, expired session) must not persist across page loads.
      const storage = getStorage();
      const storedUser = storage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setLoading(false);
      }

      const response = await apiClient.get('/api/auth/user/');
      if (response.data.success) {
        setUser(response.data.user);
        storage.setItem('user', JSON.stringify(response.data.user));
      } else {
        setUser(null);
        storage.removeItem('user');
      }
    } catch (error) {
      setUser(null);
      getStorage().removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  // `remember` defaults to true so Azure SSO (which never passes it) keeps
  // today's behavior; the password login form passes the checkbox value.
  const login = (userData, remember = true) => {
    localStorage.setItem('remember_me', remember ? 'true' : 'false');
    const storage = remember ? localStorage : sessionStorage;
    const other = remember ? sessionStorage : localStorage;
    other.removeItem('user');
    storage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = async () => {
    try {
      await apiClient.post('/api/logout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      localStorage.removeItem('remember_me');
    }
  };

  const isStaffUser = () => {
    return user?.is_staff === true;
  };

  const isNormalUser = () => {
    return user?.role === 'normal' && !user?.is_staff;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    isStaffUser,
    isNormalUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

